import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './SidePanel.css';
import EmptyState from '../common/EmptyState';
import Skeleton from '../common/Skeleton/Skeleton';
import { useConversation } from '../../state/ConversationContext';
import { useAuth } from '../../state/AuthContext';
import { userService } from '../../services/userService';
import { formatLocal } from '../../utils/dates';
import { APP_ROUTES, getChatThreadPath } from '../../routes/routingContract';
import TrialUpgradeCard from '../Billing/TrialUpgradeCard';
import StateBlock from '../common/StateBlock';

const TIER_LABELS_SP = { payg: 'Legacy PAYG', starter: 'Starter', growth: 'Growth', pro: 'Pro' };

export default function SidePanel({ onThreadSelected, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { threads, threadsLoading, threadsError, loadThreads, newConversation, currentThreadId } = useConversation();
  const { session, billingStatus } = useAuth();
  const [userInfo, setUserInfo] = React.useState(null);
  const [searchTerm, setSearchTerm] = React.useState('');
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!session?.userId) return;
        const data = await userService.getUser(session.userId);
        if (!cancelled) setUserInfo(data);
      } catch (e) {
        if (!cancelled) setUserInfo(null);
      }
    })();
    return () => { cancelled = true; };
  }, [session?.userId]);
  const displayName = React.useMemo(() => {
    const toTitle = (s) => s.replace(/\b\w+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
    const first = (userInfo?.first_name || '').trim();
    const last = (userInfo?.last_name || '').trim();
    const full = `${first} ${last}`.trim();
    const name = full || userInfo?.email || '';
    return name ? toTitle(name) : '';
  }, [userInfo?.first_name, userInfo?.last_name, userInfo?.email]);
  const avatarInitial = React.useMemo(() => {
    const first = (userInfo?.first_name || '').trim();
    const last = (userInfo?.last_name || '').trim();
    const source = first || last || userInfo?.email || '';
    return source.slice(0,1).toUpperCase();
  }, [userInfo?.first_name, userInfo?.last_name, userInfo?.email]);
  const planSubLabel = React.useMemo(() => {
    if (!billingStatus?.subscription) return '';
    const tier = billingStatus.subscription.plan_tier;
    const tierLabel = TIER_LABELS_SP[tier] || tier;
    if (tier === 'payg') return tierLabel;
    const quota = billingStatus.subscription.monthly_prompt_quota || 0;
    const used = billingStatus.usage?.prompts_used || 0;
    const remaining = Math.max(0, quota - used);
    return `${tierLabel} · ${remaining} left`;
  }, [billingStatus]);
  const handleSelect = (id) => {
    navigate(getChatThreadPath(id));
    if (onThreadSelected) onThreadSelected();
  };
  const handleNew = () => {
    newConversation();
    navigate(APP_ROUTES.chat);
    if (onThreadSelected) onThreadSelected();
  };
  const handleDashboard = () => {
    navigate(APP_ROUTES.dashboard);
    if (onThreadSelected) onThreadSelected();
  };
  const handleOpenProfile = () => {
    navigate(APP_ROUTES.profile, {
      state: { backgroundPath: location.pathname },
    });
    if (onClose) onClose();
  };
  const handleUpgrade = () => {
    navigate(APP_ROUTES.upgrade, {
      state: {
        backgroundPath: location.pathname,
        upgradeMessage: 'You can upgrade now or continue with your free trial.',
      },
    });
    if (onClose) onClose();
  };
  const sortedThreads = React.useMemo(() => {
    if (!threads || !Array.isArray(threads)) return [];
    return [...threads].sort((a, b) => {
      const aTime = new Date(a.updatedAt || a.createdAt).getTime();
      const bTime = new Date(b.updatedAt || b.createdAt).getTime();
      return bTime - aTime; // descending
    });
  }, [threads]);
  const filteredThreads = React.useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return sortedThreads;
    return sortedThreads.filter((t) => {
      const title = (t.title || 'New conversation').toLowerCase();
      return title.includes(query);
    });
  }, [searchTerm, sortedThreads]);

  const groupedThreads = React.useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterdayStart = new Date(todayStart);
    yesterdayStart.setDate(yesterdayStart.getDate() - 1);
    const last7DaysStart = new Date(todayStart);
    last7DaysStart.setDate(last7DaysStart.getDate() - 7);

    const groups = {
      today: [],
      yesterday: [],
      previous7Days: [],
      older: []
    };

    filteredThreads.forEach((thread) => {
      const threadDate = new Date(thread.updatedAt || thread.createdAt);
      
      if (threadDate >= todayStart) {
        groups.today.push(thread);
      } else if (threadDate >= yesterdayStart) {
        groups.yesterday.push(thread);
      } else if (threadDate >= last7DaysStart) {
        groups.previous7Days.push(thread);
      } else {
        groups.older.push(thread);
      }
    });

    // Count how many groups have threads
    const nonEmptyGroups = Object.values(groups).filter(g => g.length > 0).length;
    
    return { groups, showHeaders: nonEmptyGroups > 1 };
  }, [filteredThreads]);
  const isSearching = searchTerm.trim().length > 0;
  return (
    <div className="sidepanel" role="complementary" aria-label="History">
      {/* Mobile-only close button - positioned relative to sidepanel */}
      <button
        type="button"
        className="sidebar-close"
        aria-label="Close history"
        onClick={() => onClose && onClose()}
      >
        ×
      </button>
      <div className="sidepanel-body">
        <div className="sidepanel-top">
          {/* Logo + wordmark */}
          <div className="sidebar-brand">
            <span className="sidebar-logo-mark" aria-hidden="true">
              <img src={`${process.env.PUBLIC_URL}/wealth-y_logo.png`} alt="" />
            </span>
            <span className="sidebar-wordmark">Wealth-Y</span>
          </div>
          <div className="sidepanel-actions">
            <button
              type="button"
              className={`dashboard-nav${location.pathname === APP_ROUTES.dashboard ? ' active' : ''}`}
              onClick={handleDashboard}
              aria-label="Open dashboard"
            >
              Dashboard
            </button>
            <button className="new-chat" onClick={handleNew} aria-label="Start new conversation">
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
                <path d="M6.5 1v11M1 6.5h11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              New conversation
            </button>
          </div>
          <div className="sidepanel-controls">
            <label className="search-row">
              <span className="icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/>
                  <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </span>
              <input
                className="search"
                placeholder="Search chats"
                aria-label="Search history"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </label>
            <div className="sidepanel-header">Chats</div>
          </div>
        </div>
        
        
        {threadsLoading ? (
          <div className="thread-list" role="list" aria-label="Loading chats">
            {Array.from({ length: 6 }).map((_, idx) => (
              <div key={`thread-skeleton-${idx}`} className="thread-item thread-item-skeleton" role="listitem">
                <div className="thread-row">
                  <Skeleton className="thread-title-skeleton" height="16px" width="70%" />
                </div>
                <Skeleton className="thread-meta-skeleton" height="12px" width="35%" />
                <Skeleton className="thread-preview-skeleton" height="12px" width="85%" />
              </div>
            ))}
          </div>
        ) : threadsError ? (
          <StateBlock
            state="error"
            title={threadsError.title}
            message={threadsError.message}
            actionLabel={threadsError.actionLabel || 'Retry'}
            onAction={() => loadThreads()}
          />
        ) : sortedThreads.length === 0 ? (
          <EmptyState
            title="No chats yet"
            description="Kick off a new conversation and it will land here."
          />
        ) : filteredThreads.length === 0 ? (
          <EmptyState
            title="No matching conversations"
            description={isSearching ? 'Try a different search term.' : 'Start a new conversation to see it here.'}
          />
        ) : (
          <div className="thread-list" role="list">
            {groupedThreads.groups.today.length > 0 && (
              <>
                {groupedThreads.showHeaders && <div className="thread-group-header">Today</div>}
                {groupedThreads.groups.today.map((t) => (
                  <div key={t.id} role="listitem">
                    <button className={`thread-item${currentThreadId === t.id ? ' active' : ''}`} onClick={() => handleSelect(t.id)}>
                      <div className="thread-row">
                        <h3 className="thread-title" title={t.title || ''}>{t.title || 'New conversation'}</h3>
                      </div>
                      <div className="thread-meta">{formatLocal(t.updatedAt || t.createdAt)}</div>
                      <p className="thread-preview" title={t.lastMessagePreview || ''}>{t.lastMessagePreview || ''}</p>
                    </button>
                  </div>
                ))}
              </>
            )}

            {groupedThreads.groups.yesterday.length > 0 && (
              <>
                {groupedThreads.showHeaders && <div className="thread-group-header">Yesterday</div>}
                {groupedThreads.groups.yesterday.map((t) => (
                  <div key={t.id} role="listitem">
                    <button className={`thread-item${currentThreadId === t.id ? ' active' : ''}`} onClick={() => handleSelect(t.id)}>
                      <div className="thread-row">
                        <h3 className="thread-title" title={t.title || ''}>{t.title || 'New conversation'}</h3>
                      </div>
                      <div className="thread-meta">{formatLocal(t.updatedAt || t.createdAt)}</div>
                      <p className="thread-preview" title={t.lastMessagePreview || ''}>{t.lastMessagePreview || ''}</p>
                    </button>
                  </div>
                ))}
              </>
            )}

            {groupedThreads.groups.previous7Days.length > 0 && (
              <>
                {groupedThreads.showHeaders && <div className="thread-group-header">Previous 7 Days</div>}
                {groupedThreads.groups.previous7Days.map((t) => (
                  <div key={t.id} role="listitem">
                    <button className={`thread-item${currentThreadId === t.id ? ' active' : ''}`} onClick={() => handleSelect(t.id)}>
                      <div className="thread-row">
                        <h3 className="thread-title" title={t.title || ''}>{t.title || 'New conversation'}</h3>
                      </div>
                      <div className="thread-meta">{formatLocal(t.updatedAt || t.createdAt)}</div>
                      <p className="thread-preview" title={t.lastMessagePreview || ''}>{t.lastMessagePreview || ''}</p>
                    </button>
                  </div>
                ))}
              </>
            )}

            {groupedThreads.groups.older.length > 0 && (
              <>
                {groupedThreads.showHeaders && <div className="thread-group-header">Older</div>}
                {groupedThreads.groups.older.map((t) => (
                  <div key={t.id} role="listitem">
                    <button className={`thread-item${currentThreadId === t.id ? ' active' : ''}`} onClick={() => handleSelect(t.id)}>
                      <div className="thread-row">
                        <h3 className="thread-title" title={t.title || ''}>{t.title || 'New conversation'}</h3>
                      </div>
                      <div className="thread-meta">{formatLocal(t.updatedAt || t.createdAt)}</div>
                      <p className="thread-preview" title={t.lastMessagePreview || ''}>{t.lastMessagePreview || ''}</p>
                    </button>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {/* User button — opens profile modal directly */}
      <div className="user-drawer">
        <TrialUpgradeCard
          billingStatus={billingStatus}
          compact
          onUpgradeClick={handleUpgrade}
        />
        <button
          className="user-trigger as-button"
          type="button"
          aria-label="Open profile settings"
          onClick={handleOpenProfile}
        >
          <span className="avatar" aria-hidden>{avatarInitial}</span>
          <div className="user-info">
            <span className="user-label">{displayName}</span>
            {planSubLabel && <span className="user-sublabel">{planSubLabel}</span>}
          </div>
        </button>
      </div>
    </div>
  );
}
